import React from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { Target, Eye, Users, Calendar, Award, Shield } from "lucide-react";
import PageBanner from "../components/PageBanner";
import { aboutPageData } from "../data/pageData";
import { trustedBy } from "../data/mockData";

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6 } } };

const AboutPage = () => {
  const [ref1, inView1] = useInView({ triggerOnce: true, threshold: 0.15 });
  const [ref2, inView2] = useInView({ triggerOnce: true, threshold: 0.15 });
  const [ref3, inView3] = useInView({ triggerOnce: true, threshold: 0.15 });
  const [ref4, inView4] = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <>
      <PageBanner
        title="About Us"
        subtitle={aboutPageData.subheading}
        image={aboutPageData.heroImage}
        breadcrumbs={[{ label: "About Us" }]}
      />

      {/* Story Section */}
      <section className="inner-section" ref={ref1}>
        <div className="inner-container">
          <motion.div className="about-story" initial="hidden" animate={inView1 ? "visible" : "hidden"} variants={fadeUp}>
            <div className="about-story-left">
              <span className="section-subtitle">Our Journey</span>
              <h2 className="section-title">3D Printing Company in Bangalore</h2>
              <div className="section-line" />
              {aboutPageData.story.map((p, i) => (
                <p key={i} className="about-story-text">{p}</p>
              ))}
            </div>
            <div className="about-story-right">
              <div className="about-image-box">
                <img src="https://images.unsplash.com/photo-1642969164999-979483e21601?w=600&h=500&fit=crop" alt="3D Printzkart Workshop Bangalore" loading="lazy" />
                <div className="about-image-badge">
                  <span className="badge-number">2018</span>
                  <span className="badge-text">Established in Bangalore</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="inner-section bg-dark" ref={ref2}>
        <div className="inner-container">
          <div className="mv-grid">
            <motion.div className="mv-card" initial={{ opacity: 0, x: -30 }} animate={inView2 ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.6 }}>
              <Target size={40} className="mv-icon" />
              <h3>Our Mission</h3>
              <p>{aboutPageData.mission}</p>
            </motion.div>
            <motion.div className="mv-card" initial={{ opacity: 0, x: 30 }} animate={inView2 ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.6, delay: 0.2 }}>
              <Eye size={40} className="mv-icon" />
              <h3>Our Vision</h3>
              <p>{aboutPageData.vision}</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="inner-section" ref={ref3}>
        <div className="inner-container">
          <motion.div initial="hidden" animate={inView3 ? "visible" : "hidden"} variants={fadeUp}>
            <span className="section-subtitle">Our Milestones</span>
            <h2 className="section-title">The 3D Printzkart Journey</h2>
            <div className="section-line" />
          </motion.div>
          <div className="timeline">
            {aboutPageData.milestones.map((m, i) => (
              <motion.div key={i} className={`timeline-item ${i % 2 === 0 ? 'left' : 'right'}`}
                initial={{ opacity: 0, y: 20 }} animate={inView3 ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.4, delay: i * 0.1 }}>
                <div className="timeline-dot"><Calendar size={14} /></div>
                <div className="timeline-content">
                  <span className="timeline-year">{m.year}</span>
                  <p>{m.event}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="inner-section bg-light" ref={ref4}>
        <div className="inner-container">
          <motion.div initial="hidden" animate={inView4 ? "visible" : "hidden"} variants={fadeUp}>
            <span className="section-subtitle">Meet the Experts</span>
            <h2 className="section-title">The Team Behind 3D Printzkart</h2>
            <div className="section-line" />
          </motion.div>
          <div className="team-grid">
            {aboutPageData.team.map((member, i) => (
              <motion.div key={i} className="team-card" initial={{ opacity: 0, y: 20 }}
                animate={inView4 ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.4, delay: i * 0.1 }}>
                <div className="team-avatar">
                  <Users size={32} />
                </div>
                <h4>{member.name}</h4>
                <span className="team-role">{member.role}</span>
                <p>{member.bio}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Trusted By */}
      <section className="trusted-section">
        <div className="trusted-inner">
          <h3 className="trusted-title">Trusted By Industry Leaders</h3>
          <div className="trusted-scroll">
            <div className="trusted-track">
              {[
                '1.png', '2.jpg', '3.png', '4.png', '5.png', '6.png', '7.jpg', '8.png', '9.jpg', '10.png',
                '11.png', '12.png', '13.png', 'Picture14.jpg', 'Picture15.png', 'Picture16.png', 'Picture17.png',
                'Picture18.png', 'Picture19.png', 'Picture20.png', 'Picture21.jpg', 'Picture22.png', 'Picture23.jpg',
                'Picture24.png', 'Picture25.png', 'Picture26.png', 'Picture27.png', 'Picture28.jpg', 'Picture29.jpg',
                'Picture30.png', 'Picture31.png', 'Picture32.png', 'Picture33.jpg', 'Picture34.jpg', 'Picture35.png',
                'Picture36.png', 'Picture37.jpg', 'Picture38.png', 'Picture39.png', 'Picture40.jpg'
              ].concat([
                '1.png', '2.jpg', '3.png', '4.png', '5.png', '6.png', '7.jpg', '8.png', '9.jpg', '10.png',
                '11.png', '12.png', '13.png', 'Picture14.jpg', 'Picture15.png', 'Picture16.png', 'Picture17.png',
                'Picture18.png', 'Picture19.png', 'Picture20.png', 'Picture21.jpg', 'Picture22.png', 'Picture23.jpg',
                'Picture24.png', 'Picture25.png', 'Picture26.png', 'Picture27.png', 'Picture28.jpg', 'Picture29.jpg',
                'Picture30.png', 'Picture31.png', 'Picture32.png', 'Picture33.jpg', 'Picture34.jpg', 'Picture35.png',
                'Picture36.png', 'Picture37.jpg', 'Picture38.png', 'Picture39.png', 'Picture40.jpg'
              ]).map((logo, i) => (
                <div key={i} className="trusted-logo">
                  <img src={`/logos/${logo}`} alt={`Company ${i + 1}`} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default AboutPage;
